import { snakeCase, startCase } from "lodash";
import { Config, Mixpanel, OverridedMixpanel } from "mixpanel-browser";
import React, { useLayoutEffect } from "react";

import { sanitizeMixpanelProperties } from "./mixpanel-properties";

const PROJECT_TOKEN = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN;
const PROXY_DOMAIN = import.meta.env.VITE_MIXPANEL_PROXY_DOMAIN;

declare global {
  interface Window {
    mixpanel: OverridedMixpanel;
  }
}

const mixpanel = window.mixpanel || {};

const noopProxyHandler = {
  get: function (target: any, key: string | symbol): any {
    if (typeof target[key] === "function") {
      return function () {};
    }
    if (typeof target[key] === "object") {
      return new Proxy(target[key], noopProxyHandler);
    }
    return target[key];
  },
};

const noopMixpanel = new Proxy(mixpanel, noopProxyHandler);

// Missing from @types/mixpanel-browser 2.38; on_track runs on every event, including the library's own, after its properties are merged.
type MixpanelHooks = {
  on_track?: (
    eventName: string,
    properties: Record<string, unknown>,
  ) => [string, Record<string, unknown>];
};

export const MixpanelContext = React.createContext<Mixpanel>(noopMixpanel);

function normalizeEventName(event_name: string) {
  return startCase(event_name.trim());
}

type Dict = { [key: string]: any };
function normalizePropertyNames(properties: Dict) {
  return Object.entries(properties).reduce<Dict>((acc, [key, val]) => {
    acc[snakeCase(key.trim())] = val;
    return acc;
  }, {});
}

/**
 * Simple proxy that proxies Mixpanel.track() to normalize property names
 */
const createMixpanelProxy = (instance: Mixpanel) =>
  new Proxy(instance, {
    get(target: typeof instance, prop: keyof typeof instance) {
      /**
       * Override the `time_event` and `track` methods to normalize event names
       * and property names.
       * This is useful to ensure consistent naming conventions across events.
       */
      if (prop === "time_event") {
        const timeEventOverride: typeof instance.time_event = (event_name) => {
          return target[prop](normalizeEventName(event_name));
        };
        return timeEventOverride;
      }
      if (prop === "track") {
        const trackOverride: typeof instance.track = (
          event_name,
          properties,
          optionsOrCallback,
          callback,
        ) => {
          target[prop](
            normalizeEventName(event_name),
            properties == null
              ? properties
              : normalizePropertyNames(properties),
            optionsOrCallback,
            callback,
          );
        };
        return trackOverride;
      }
      return target[prop];
    },
  });

export function MixpanelProvider({ children }: { children: React.ReactNode }) {
  const [instance, setInstance] = React.useState<Mixpanel>(noopMixpanel);

  useLayoutEffect(() => {
    if (!(PROJECT_TOKEN && PROXY_DOMAIN)) return;
    const config: Partial<Config> & { hooks: MixpanelHooks } = {
      api_host: PROXY_DOMAIN,
      debug: !import.meta.env.PROD,
      loaded: function (mixpanel) {
        unregisterObsoleteProperties(mixpanel);
        const releaseId = import.meta.env.RELEASE_ID;
        if (releaseId) {
          mixpanel.register({ release_id: releaseId });
        }
        setInstance(createMixpanelProxy(mixpanel));
      },
      // Ignore the web browser's Do Not Track setting
      ignore_dnt: true,
      hooks: {
        on_track: (eventName, properties) => [
          eventName,
          sanitizeMixpanelProperties(eventName, properties),
        ],
      },
    };
    mixpanel.init(PROJECT_TOKEN, config);
  }, []);
  return (
    <MixpanelContext.Provider value={instance}>
      {children}
    </MixpanelContext.Provider>
  );
}

export function useMixpanel() {
  return React.useContext(MixpanelContext);
}

/**
 * Unregister a few super properties that are no longer used.
 * Registered properties are persisted in cookie/localStorage and
 * will keep being sent to Mixpanel until they are unregistered.
 */
function unregisterObsoleteProperties(mixpanel: Mixpanel) {
  mixpanel.unregister("releaseId");
}
